import React, { useState, useEffect, useRef } from 'react';
import watch from 'redux-watch';
import { wrapFunctionalComponent } from 'utils/framework';
import store from 'store/Store';
import MediaUtils from 'utils/Media';
const { Media } = MediaUtils;
import {
    Box, Button, Flex, Image, Text, Link, Divider
} from 'components/ui';
import Empty from 'constants/empty';
import Radio from 'components/Inputs/Radio/Radio';
import TextInput from 'components/Inputs/TextInput/TextInput';
import localeUtils from 'utils/LanguageLocale';
import FormValidator from 'utils/FormValidator';
import CommunicationLanguagePrefsEN from 'components/RichProfile/MyAccount/MailingPrefs/CommunicationLanguagePrefs/locales/CommunicationLanguagePrefs_en_US';
import CommunicationLanguagePrefsFR from 'components/RichProfile/MyAccount/MailingPrefs/CommunicationLanguagePrefs/locales/CommunicationLanguagePrefs_fr_CA';
import profileApi from 'services/api/profile';
import bccEmailSMSOptInService from 'services/api/bccEmailSmsOptIn';

const getText = localeUtils.getLocalisedDataFromResource(CommunicationLanguagePrefsEN, CommunicationLanguagePrefsFR);

const { COUNTRIES, LANGUAGES, LANGUAGE_CODES } = localeUtils;
const { englishUSA, englishCA, frenchCA } = LANGUAGE_CODES;

const LANGUAGE_OPTIONS = [
    {
        value: englishUSA,
        country: COUNTRIES.US,
        language: LANGUAGES.EN
    },
    {
        value: englishCA,
        country: COUNTRIES.CA,
        language: LANGUAGES.EN
    },
    {
        value: frenchCA,
        country: COUNTRIES.CA,
        language: LANGUAGES.FR
    }
];

function isLocaleChange(previousLocale, currentLocale) {
    return previousLocale !== '' && previousLocale !== currentLocale;
}

const resubscribeButtonStyle = {
    minWidth: '12em',
    paddingTop: 0,
    paddingBottom: 0,
    lineHeight: '1.8',
    backgroundColor: 'white'
};

// Banner that appears when the user has unsubscribed from SMS and has changed their preferred language.
function ResubscribeBanner({
    language, onDismiss, onSubscribe, isUserSMSOptedIn, smsUnsubscribeEnabledFlag
}) {
    return (
        smsUnsubscribeEnabledFlag &&
        isUserSMSOptedIn && (
            <Box
                paddingX={3}
                paddingY={3}
                backgroundColor='nearWhite'
            >
                <Text is='p'>
                    {getText('unsubscribedFromSMS')} {language}?
                </Text>
                <Box
                    display='flex'
                    justifyContent='flex-end'
                    alignItems='center'
                    marginTop={3}
                    gap={3}
                >
                    <Link
                        color='blue'
                        onClick={onDismiss}
                    >
                        {getText('dismiss')}
                    </Link>
                    <Button
                        variant='secondary'
                        onClick={onSubscribe}
                        css={resubscribeButtonStyle}
                    >
                        {getText('subscribe')}
                    </Button>
                </Box>
            </Box>
        )
    );
}

function CommunicationLanguagePrefs({
    viewMode,
    onExpand,
    onLocaleChange,
    onUpdatedPreferences,
    onUnsubscribe,
    onSubscribe,
    smsUnsubscribeEnabledFlag
}) {
    const [editMode, setEditMode] = useState(false);
    const [selectedLocale, setSelectedLocale] = useState('');
    const [isUserSMSOptedIn, setIsUserSMSOptedIn] = useState(false);
    const [zipCode, setZipCode] = useState('');
    const [prefs, setPrefs] = useState(null); // The user's current mailing preferences.
    const [isMailingPrefsReceived, setIsMailingPrefsReceived] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [showResubscribeBanner, setShowResubscribeBanner] = useState(false);
    const [savedLocale, setSavedLocale] = useState('');
    const [primaryPhone, setPrimaryPhone] = useState(null); // The user's primary phone number.
    const profileIdRef = useRef(null);
    const innerUnsubscribeRef = useRef(null);
    const zipPostalCodeInputRef = useRef(null);
    const previousLocaleRef = useRef('');
    const previousZipCodeRef = useRef('');

    const smsOptInPageTypes = {
        optIn: 'SMS_optin',
        optOut: 'SMS_optout'
    };

    useEffect(() => {
        const subscribeToMailingPrefs = () => {
            const mailingPrefsWatch = watch(store.getState, 'mailingPreferences');
            innerUnsubscribeRef.current = store.subscribe(
                mailingPrefsWatch(mailingPreferences => {
                    try {
                        setPrefs(mailingPreferences.mailingPreferences);
                        setIsMailingPrefsReceived(true);
                    } catch (error) {
                        Sephora.logger.verbose('Error in CommunicationLanguagePrefs', error);
                    }
                })
            );
        };

        const handleUser = watchedUser => {
            if (profileIdRef.current !== watchedUser.profileId) {
                profileIdRef.current = watchedUser.profileId;

                if (innerUnsubscribeRef.current) {
                    innerUnsubscribeRef.current();
                }

                subscribeToMailingPrefs();
            }

            setIsUserSMSOptedIn(!!watchedUser.isUserSMSOptedIn);
            setPrimaryPhone(watchedUser.primaryPhone); // Will be used to handle the un-subscription.
        };

        handleUser(store.getState().user);

        const userWatch = watch(store.getState, 'user');
        const unsubscribeUser = store.subscribe(userWatch(handleUser));

        return () => {
            unsubscribeUser();

            if (innerUnsubscribeRef.current) {
                innerUnsubscribeRef.current();
            }
        };
    }, []);

    useEffect(() => {
        if (viewMode) {
            setEditMode(false);
        }
    }, [viewMode]);

    useEffect(() => {
        if (!prefs || editMode) {
            return;
        }

        // Pulls the user's subscriptions
        const userSubscriptions = prefs?.userSubscriptions?.subscriptions || [];
        const preferredLanguagePrefs = prefs?.preferredLanguagePrefs || {};

        // Pulls the Zipcode from the current user's subscriptions, if available. If not, it will default to an empty string.
        const zipCodeFromSubscriptions = userSubscriptions.filter(s => (s?.subscriptionZip || '') !== '');

        const subscriptionCountry = preferredLanguagePrefs?.subscriptionPreferredCountry || '';
        const subscriptionLanguage = preferredLanguagePrefs?.subscriptionPreferredLanguage || '';
        const subscriptionLocale = `${subscriptionLanguage.toLowerCase()}_${subscriptionCountry}`;
        const subscriptionZipCode = zipCodeFromSubscriptions[0]?.subscriptionZip || '';

        setZipCode(subscriptionZipCode);

        if (subscriptionCountry !== '' || subscriptionLanguage !== '') {
            setSelectedLocale(subscriptionLocale);
        }

        onLocaleChange?.({
            subscriptionPreferredLanguage: subscriptionLanguage,
            subscriptionPreferredCountry: subscriptionCountry,
            subscriptionPreferredZipCode: subscriptionZipCode
        });
    }, [prefs]);

    const switchToEditMode = () => {
        previousLocaleRef.current = selectedLocale;
        previousZipCodeRef.current = zipCode;
        setShowResubscribeBanner(false);
        setEditMode(true);
        onExpand();
    };

    const hasLocaleChanged = editMode && selectedLocale !== '' && selectedLocale !== previousLocaleRef.current;

    const handleCancelClick = () => {
        setSelectedLocale(previousLocaleRef.current);
        setZipCode(previousZipCodeRef.current);
        setFormErrors({});
        setEditMode(false);
    };

    const callSMSOptInService = async ({
        phoneNumber,
        locale,
        pageName,
        pageType = smsOptInPageTypes.optIn,
        callbackFunction = null,
        callbackfunctionValue = null
    }) => {
        try {
            await bccEmailSMSOptInService.submitEmailSMSOptInForm({
                phoneNumber,
                locale,
                pageName,
                pageType,
                type: 'SMS'
            });

            callbackFunction?.(callbackfunctionValue);
        } catch (err) {
            Sephora.logger.error(`Error submitting SMS ${pageType} request: `, { err });
        }
    };

    const unsubscribeFromSMS = async params => {
        const { locale, afterOptOutFunction } = params || {};
        const phone = primaryPhone || Empty.String;

        if (!phone || phone === Empty.String) {
            Sephora.logger.error('SMS opt-out aborted: no phone number available for the current user.');
        }

        if (isUserSMSOptedIn && phone && phone !== Empty.String) {
            const pageName = digitalData?.page?.pageInfo?.pageName || Empty.String;

            await callSMSOptInService({
                phoneNumber: phone,
                locale: locale || selectedLocale,
                pageName,
                pageType: smsOptInPageTypes.optOut,
                callbackFunction: onUnsubscribe,
                callbackfunctionValue: true
            });
        }

        afterOptOutFunction?.();
    };

    const subscribeForSMS = async () => {
        const pageName = digitalData?.page?.pageInfo?.pageName || Empty.String;

        if (!primaryPhone || primaryPhone === Empty.String) {
            Sephora.logger.error('SMS opt-in aborted: no phone number available for the current user.');

            return;
        }

        await callSMSOptInService({
            phoneNumber: primaryPhone,
            locale: selectedLocale,
            pageName,
            pageType: smsOptInPageTypes.optIn,
            callbackFunction: onSubscribe,
            callbackfunctionValue: true
        });
    };

    const savePrefs = async prefsToSave => {
        profileApi.setCommunicationPreferences(prefsToSave).then(() => {
            // Calls the delegated function to make an api call for an update on the user's preferences.
            onUpdatedPreferences?.();
        });
    };

    const handleSaveClick = e => {
        e.preventDefault();
        setFormErrors({});

        const isValid = zipPostalCodeInputRef.current?.validateError() === null;

        if (isValid && selectedLocale !== '') {
            const localeChanged = isLocaleChange(previousLocaleRef.current, selectedLocale);

            setEditMode(false);

            // Looks for the language code ('en_US') that matches the selected locale stored in the state
            // and extracts the language ('EN') and the country ('US') separately.
            const selectedLocaleOptions = LANGUAGE_OPTIONS.find(opt => opt.value === selectedLocale);
            const subscriptionPreferredCountry = selectedLocaleOptions?.country || '';
            const subscriptionPreferredLanguage = selectedLocaleOptions?.language || '';
            const subscriptionPreferredLanguageCode = selectedLocaleOptions?.value || ''; // Language Code ('en_US').

            onLocaleChange?.({
                subscriptionPreferredLanguage: subscriptionPreferredLanguageCode,
                subscriptionPreferredCountry,
                subscriptionPreferredZipCode: zipCode
            });

            const prefsToSave = {
                ...prefs,
                subscriptionPreferredCountry: subscriptionPreferredCountry,
                subscriptionPreferredLanguage: subscriptionPreferredLanguage,
                subscriptionPreferredZipCode: zipCode
            };

            if (smsUnsubscribeEnabledFlag && localeChanged) {
                setSavedLocale(selectedLocale);
                setShowResubscribeBanner(true);

                unsubscribeFromSMS({
                    locale: previousLocaleRef.current,
                    afterOptOutFunction: () => {
                        savePrefs(prefsToSave);
                    }
                });
            } else {
                savePrefs(prefsToSave);
            }
        } else {
            setEditMode(true);
        }
    };

    const getLanguageText = locale => {
        switch (locale) {
            case englishUSA:
                return getText('usEnglish');
            case englishCA:
                return getText('canadaEnglish');
            case frenchCA:
                return getText('canadaFrench');
            default:
                return '';
        }
    };

    // Receives the language code, ie. 'en_US'.
    const handleLocaleChange = selectedPrefOption => {
        const newOption = LANGUAGE_OPTIONS.find(opt => opt.value === selectedPrefOption);

        // From state.
        const currentCountry = selectedOption?.country;

        // Resets the Zipcode field.
        if (newOption?.country !== currentCountry) {
            setZipCode('');
        }

        // Stores the language code in the state ('en_US').
        setSelectedLocale(selectedPrefOption);
    };

    const editModeSection = () => {
        return (
            <Box
                is='form'
                action=''
                noValidate
                onSubmit={handleSaveClick}
            >
                {LANGUAGE_OPTIONS.map(option => (
                    <Box
                        key={option.value}
                        display='flex'
                        alignItems='center'
                        marginBottom={2}
                    >
                        <Radio
                            name='marketingLanguage'
                            checked={selectedLocale === option.value}
                            value={option.value}
                            onClick={() => {
                                if (selectedLocale === option.value) {
                                    setSelectedLocale('');
                                    setZipCode('');
                                }
                            }}
                            onChange={() => {
                                handleLocaleChange(option.value);
                            }}
                        >
                            <Box
                                display='inline-flex'
                                alignItems='center'
                            >
                                <Image
                                    src={localeUtils.getCountryFlagImage(option.country)}
                                    width='1.25em'
                                    height='1em'
                                    display='inline-block'
                                    marginRight={2}
                                />
                                {getLanguageText(option.value)}
                            </Box>
                        </Radio>
                    </Box>
                ))}

                <Box marginTop={4}>
                    <Text
                        is='p'
                        marginBottom={2}
                    >
                        {getText(enterCodeTextKey)}
                    </Text>
                    <TextInput
                        label={zipInputPlaceholder}
                        name='zipCode'
                        ref={component => {
                            if (component !== null) {
                                zipPostalCodeInputRef.current = component;
                            }
                        }}
                        onChange={e => {
                            setZipCode(e.currentTarget.value);
                            setFormErrors(prev => ({ ...prev, zipPostalCode: null }));
                        }}
                        validate={zipPostalCode => {
                            if (FormValidator.isEmpty(zipPostalCode)) {
                                return null;
                            }

                            if (!FormValidator.isValidZipCode(zipPostalCode, isCanada ? 'CA' : 'US')) {
                                return getText(isCanada ? 'invalidPostalCode' : 'invalidZipCode');
                            }

                            return formErrors.zipPostalCode || null;
                        }}
                        value={zipCode}
                    />
                    {isCanada && (
                        <Text
                            is='p'
                            fontSize='xs'
                            color='gray'
                            lineHeight='tight'
                        >
                            {getText('canadaLangCountryCopy')}
                        </Text>
                    )}
                </Box>

                {smsUnsubscribeEnabledFlag && hasLocaleChanged && (
                    <Box
                        marginTop={4}
                        paddingX={3}
                        paddingY={3}
                        backgroundColor='nearWhite'
                    >
                        <Text is='p'>{getText('resubscribeToTextUpdates')}</Text>
                    </Box>
                )}

                <Flex
                    marginTop={5}
                    gap={3}
                >
                    <Box flex='1'>
                        <Button
                            variant='secondary'
                            block={true}
                            onClick={handleCancelClick}
                        >
                            {getText('cancel')}
                        </Button>
                    </Box>
                    <Box flex='1'>
                        <Button
                            variant='primary'
                            block={true}
                            type='submit'
                            disabled={!selectedLocale}
                        >
                            {getText('save')}
                        </Button>
                    </Box>
                </Flex>
            </Box>
        );
    };

    const isReady = isMailingPrefsReceived;

    const showBanner = showResubscribeBanner;

    const selectedOption = LANGUAGE_OPTIONS.find(opt => opt.value === selectedLocale);
    const isCanada = selectedOption?.country === COUNTRIES.CA;
    const flagImage = localeUtils.getCountryFlagImage(selectedOption?.country || COUNTRIES.US);
    const zipCodeLabelKey = isCanada ? 'postalCodeLabel' : 'zipCodeLabel';
    const enterCodeTextKey = isCanada ? 'enterPostalCode' : 'enterZipCode';
    const zipInputPlaceholder = isCanada ? getText('postalCode') : getText('zipCode');

    return prefs && isReady ? (
        <Box>
            {/* Mobile */}
            <Media lessThan='md'>
                <Box paddingY={5}>
                    {/* Title Section */}
                    <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                    >
                        <Text
                            is='h2'
                            fontSize='md'
                            fontWeight='bold'
                            lineHeight='tight'
                        >
                            {getText('marketingLanguage')}
                        </Text>
                        {!editMode && (
                            <Link
                                color='blue'
                                marginY={-2}
                                paddingY={2}
                                onClick={switchToEditMode}
                            >
                                {getText('edit')}
                            </Link>
                        )}
                    </Box>
                    <Divider marginY={3} />
                    <Text is='p'>{getText('marketingTitle')}</Text>
                    {/* Body section */}
                    {!editMode && (
                        <Box marginBottom={3}>
                            <Image
                                src={flagImage}
                                width='1.25em'
                                height='1em'
                                display='inline-block'
                                marginRight={2}
                                css={{ verticalAlign: 'middle' }}
                            />
                            <Text>{getLanguageText(selectedLocale)}</Text>
                            <Text is='p'>
                                <b>{getText(zipCodeLabelKey)}</b> {zipCode || getText('none')}
                            </Text>
                            <Text is='p'>{getText(enterCodeTextKey)}</Text>
                        </Box>
                    )}
                    {editMode && editModeSection()}
                    {showBanner && (
                        <ResubscribeBanner
                            isUserSMSOptedIn={isUserSMSOptedIn}
                            language={getLanguageText(savedLocale)}
                            onDismiss={() => setShowResubscribeBanner(false)}
                            onSubscribe={() => {
                                setShowResubscribeBanner(false);
                                subscribeForSMS();
                            }}
                            smsUnsubscribeEnabledFlag={smsUnsubscribeEnabledFlag}
                        />
                    )}
                </Box>
            </Media>

            {/* Desktop */}
            <Media greaterThan='sm'>
                <Flex
                    gap={3}
                    alignItems='baseline'
                    paddingY={5}
                >
                    <Box
                        width='16em'
                        flexShrink={0}
                    >
                        <Text
                            is='h2'
                            fontSize='md'
                            fontWeight='bold'
                            lineHeight='tight'
                        >
                            {getText('marketingLanguage')}
                        </Text>
                    </Box>
                    <Box flex='1'>
                        <Text is='p'>{getText('marketingTitle')}</Text>
                        {!editMode && (
                            <Box>
                                {selectedLocale === '' ? (
                                    <Text is='p'>{getText('noCountryOrLanguageSelected')}</Text>
                                ) : (
                                    <Box
                                        display='flex'
                                        alignItems='center'
                                        marginBottom={2}
                                    >
                                        <Image
                                            src={flagImage}
                                            width='1.25em'
                                            height='1em'
                                            display='inline-block'
                                            marginRight={2}
                                        />
                                        <Text>{getLanguageText(selectedLocale)}</Text>
                                    </Box>
                                )}

                                <Text is='p'>
                                    <b>{getText(zipCodeLabelKey)}</b> {zipCode || getText('none')}
                                </Text>
                                <Text is='p'>{getText(enterCodeTextKey)}</Text>
                            </Box>
                        )}
                        {editMode && editModeSection()}
                        {showBanner && (
                            <ResubscribeBanner
                                isUserSMSOptedIn={isUserSMSOptedIn}
                                language={getLanguageText(savedLocale)}
                                onDismiss={() => setShowResubscribeBanner(false)}
                                onSubscribe={() => {
                                    setShowResubscribeBanner(false);
                                    subscribeForSMS();
                                }}
                            />
                        )}
                    </Box>
                    <Box
                        width='4em'
                        textAlign='right'
                        lineHeight='tight'
                        flexShrink={0}
                    >
                        {editMode || (
                            <Link
                                color='blue'
                                marginY={-2}
                                paddingY={2}
                                onClick={switchToEditMode}
                            >
                                {getText('edit')}
                            </Link>
                        )}
                    </Box>
                </Flex>
            </Media>
        </Box>
    ) : (
        <></>
    );
}

export default wrapFunctionalComponent(CommunicationLanguagePrefs, 'CommunicationLanguagePrefs');
